import { useEffect, useState, useCallback } from "react";
import { api, fmtINR, downloadFile } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { DownloadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const JOB_STATUSES = ["requested", "assigned", "accepted", "in_progress", "completed", "rejected", "cancelled"];
const SELL_STATUSES = ["pending", "scheduled", "picked_up", "qc_in_progress", "offer_shared", "price_accepted", "completed", "cancelled"];

const ExportBtn = ({ onClick, testid, label }) => (
  <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={onClick} data-testid={testid}>
    <DownloadSimple size={15} /> {label || "Export Excel"}
  </Button>
);

export default function Reports() {
  const [gst, setGst] = useState([]);
  const [ledger, setLedger] = useState([]);

  useEffect(() => {
    api.get("/reports/gst-summary").then((r) => setGst(r.data)).catch(() => {});
    api.get("/reports/ledger").then((r) => setLedger(r.data)).catch(() => {});
  }, []);

  const chartData = gst.map((g) => ({ name: g.txn_type, Total: g.total, Tax: g.cgst + g.sgst + g.igst }));

  const doExport = async (path, filename, params) => {
    try { await downloadFile(path, filename, params); }
    catch { toast.error("Could not export. Please try again."); }
  };

  return (
    <>
      <PageHeader title="Reports" subtitle="Job & Sell Device reports, GST, ledger — with Excel export" />
      <div className="p-8 space-y-6">
        <Tabs defaultValue="jobs">
          <TabsList className="rounded-sm">
            <TabsTrigger value="jobs" className="rounded-sm" data-testid="tab-job-report">Job Report</TabsTrigger>
            <TabsTrigger value="sell" className="rounded-sm" data-testid="tab-sell-report">Sell Device Report</TabsTrigger>
            <TabsTrigger value="gst" className="rounded-sm" data-testid="tab-gst">GST Summary</TabsTrigger>
            <TabsTrigger value="ledger" className="rounded-sm" data-testid="tab-ledger">Ledger</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs" className="pt-4">
            <JobReport onExport={doExport} />
          </TabsContent>

          <TabsContent value="sell" className="pt-4">
            <SellReport onExport={doExport} />
          </TabsContent>

          <TabsContent value="gst" className="space-y-6 pt-4">
            <div className="flex justify-end">
              <ExportBtn testid="export-gst" onClick={() => doExport("/reports/gst-summary/export", "fixigo_gst_summary.xlsx")} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {["sale", "purchase", "receipt", "payment"].map((tp) => {
                const r = gst.find((g) => g.txn_type === tp);
                return (
                  <Card key={tp} className="rounded-sm p-6 border-border" data-testid={`gst-card-${tp}`}>
                    <p className="label-caps capitalize">{tp}</p>
                    <p className="font-heading text-2xl font-bold mt-2">{fmtINR(r?.total || 0)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Tax: {fmtINR((r?.cgst || 0) + (r?.sgst || 0) + (r?.igst || 0))}</p>
                  </Card>
                );
              })}
            </div>
            <Card className="rounded-sm border-border p-6">
              <h3 className="font-heading font-semibold mb-4">Value by Transaction Type</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => fmtINR(v)} />
                  <Bar dataKey="Total" fill="#002FA7" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Tax" fill="#D97706" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <div className="border border-border rounded-sm bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    <TableHead>Type</TableHead><TableHead className="text-right">Count</TableHead>
                    <TableHead className="text-right">Taxable</TableHead><TableHead className="text-right">CGST</TableHead>
                    <TableHead className="text-right">SGST</TableHead><TableHead className="text-right">IGST</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gst.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No data</TableCell></TableRow>}
                  {gst.map((g) => (
                    <TableRow key={g.txn_type}>
                      <TableCell><StatusBadge value={g.txn_type} /></TableCell>
                      <TableCell className="text-right">{g.count}</TableCell>
                      <TableCell className="text-right font-mono">{fmtINR(g.taxable)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtINR(g.cgst)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtINR(g.sgst)}</TableCell>
                      <TableCell className="text-right font-mono">{fmtINR(g.igst)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{fmtINR(g.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="ledger" className="space-y-4 pt-4">
            <div className="flex justify-end">
              <ExportBtn testid="export-ledger" onClick={() => doExport("/reports/ledger/export", "fixigo_ledger.xlsx")} />
            </div>
            <div className="border border-border rounded-sm bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    <TableHead>Type</TableHead><TableHead>Party</TableHead><TableHead>Date</TableHead>
                    <TableHead className="text-right">Taxable</TableHead><TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="ledger-table">
                  {ledger.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No transactions</TableCell></TableRow>}
                  {ledger.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell><StatusBadge value={t.txn_type} /></TableCell>
                      <TableCell className="font-semibold">{t.party_name}</TableCell>
                      <TableCell className="text-sm">{t.date || "—"}</TableCell>
                      <TableCell className="text-right font-mono">{fmtINR(t.taxable_amount)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{fmtINR(t.total_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function JobReport({ onExport }) {
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ partner_id: "all", status: "all", city: "all", order_type: "all" });
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "", dir: "asc" });

  const COLS = [
    { k: "order_type", label: "Order Type" }, { k: "b2b_partner_name", label: "Partner (B2B)" },
    { k: "b2b_partner_order_id", label: "Partner Order Id" }, { k: "booking_code", label: "Booking Id" },
    { k: "service_initial", label: "Service (Initial)" }, { k: "customer_name", label: "Customer" },
    { k: "address", label: "Address" }, { k: "pincode", label: "Pincode" }, { k: "city", label: "City" },
    { k: "state", label: "State" }, { k: "contact_no", label: "Contact No" }, { k: "partner_name", label: "Assigned Partner" },
    { k: "status", label: "Status" }, { k: "order_amount", label: "Order Amt", num: true },
    { k: "service_updated", label: "Updated Service" }, { k: "final_amount", label: "Final Amt", num: true },
    { k: "partner_amount", label: "Partner Amt", num: true }, { k: "additional_charged", label: "Additional Charges", num: true },
    { k: "additional_paid", label: "Additional Collected", num: true },
    { k: "preferred_service_date", label: "Preferred Date" }, { k: "preferred_slot", label: "Preferred Slot" },
    { k: "customer_remarks", label: "Remarks" }, { k: "maps_location", label: "Maps Location" },
    { k: "add_charges_to", label: "Add. Charges To" }, { k: "add_charges_status", label: "Add. Charges Status" },
    { k: "order_payment_status", label: "Order Payment Status" },
  ];

  const toggleSort = (k) => setSort((s) => s.key === k ? { key: k, dir: s.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" });

  const params = useCallback(() => {
    const p = {};
    if (f.partner_id !== "all") p.partner_id = f.partner_id;
    if (f.status !== "all") p.status = f.status;
    if (f.city !== "all") p.city = f.city;
    if (f.order_type !== "all") p.order_type = f.order_type;
    return p;
  }, [f]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/reports/jobs", { params: params() })
      .then((r) => setRows(r.data)).catch(() => toast.error("Failed to load job report"))
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
    api.get("/jobs/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const view = (() => {
    let out = rows;
    const term = q.trim().toLowerCase();
    if (term) out = out.filter((r) => COLS.some((c) => String(r[c.k] ?? "").toLowerCase().includes(term)));
    if (sort.key) {
      const col = COLS.find((c) => c.k === sort.key);
      out = [...out].sort((a, b) => {
        let av = a[sort.key], bv = b[sort.key];
        if (col?.num) { av = Number(av) || 0; bv = Number(bv) || 0; return sort.dir === "asc" ? av - bv : bv - av; }
        av = String(av ?? "").toLowerCase(); bv = String(bv ?? "").toLowerCase();
        return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return out;
  })();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Select value={f.order_type} onValueChange={(v) => setF({ ...f, order_type: v })}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="jobrep-order-type"><SelectValue placeholder="Order Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Order Types</SelectItem>
            <SelectItem value="d2c">D2C</SelectItem>
            <SelectItem value="b2b">B2B</SelectItem>
          </SelectContent>
        </Select>
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="jobrep-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {JOB_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={f.city} onValueChange={(v) => setF({ ...f, city: v })}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="jobrep-city"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={f.partner_id} onValueChange={(v) => setF({ ...f, partner_id: v })}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="jobrep-partner"><SelectValue placeholder="Partner" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Partners</SelectItem>
            {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="w-48 rounded-sm" data-testid="jobrep-search" />
        <div className="ml-auto flex gap-2">
          <ExportBtn testid="export-job-report" onClick={() => onExport("/reports/jobs/export", "fixigo_job_report.xlsx", params())} />
          <ExportBtn testid="export-job-report-csv" label="CSV" onClick={() => onExport("/reports/jobs/export", "fixigo_job_report.csv", { ...params(), format: "csv" })} />
        </div>
      </div>

      <div className="border border-border rounded-sm bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              {COLS.map((c) => (
                <TableHead key={c.k} onClick={() => toggleSort(c.k)} className={`cursor-pointer select-none whitespace-nowrap ${c.num ? "text-right" : ""}`} data-testid={`jobrep-col-${c.k}`}>
                  {c.label}{sort.key === c.k ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody data-testid="job-report-table">
            {loading && <TableRow><TableCell colSpan={COLS.length} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && view.length === 0 && <TableRow><TableCell colSpan={COLS.length} className="text-center py-8 text-muted-foreground">No jobs found</TableCell></TableRow>}
            {!loading && view.map((r, i) => (
              <TableRow key={(r.booking_code || "") + i} data-testid={`job-report-row-${i}`}>
                {COLS.map((c) => {
                  if (c.k === "order_type") return <TableCell key={c.k}><span className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${r.order_type === "B2B" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>{r.order_type}</span></TableCell>;
                  if (c.k === "status") return <TableCell key={c.k}><StatusBadge value={r.status} /></TableCell>;
                  if (c.k === "maps_location") return <TableCell key={c.k} className="text-xs">{r.maps_location ? <a href={r.maps_location} target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline">Open ↗</a> : "—"}</TableCell>;
                  if (c.num) return <TableCell key={c.k} className="text-right font-mono whitespace-nowrap">{r[c.k] === "" || r[c.k] == null ? "—" : fmtINR(r[c.k])}</TableCell>;
                  const val = r[c.k];
                  return <TableCell key={c.k} className={`text-sm ${["address", "service_updated", "customer_remarks"].includes(c.k) ? "max-w-[180px] truncate" : "whitespace-nowrap"}`} title={typeof val === "string" ? val : ""}>{val || "—"}</TableCell>;
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SellReport({ onExport }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const params = useCallback(() => (status !== "all" ? { status } : {}), [status]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/reports/sell-devices", { params: params() })
      .then((r) => setRows(r.data)).catch(() => toast.error("Failed to load sell device report"))
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return [r.booking_id, r.customer_name, r.contact_no, r.category, r.product_type].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search booking / customer / product"
            className="w-64 rounded-sm pl-8" data-testid="sellrep-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 rounded-sm" data-testid="sellrep-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {SELL_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <ExportBtn testid="export-sell-report" onClick={() => onExport("/reports/sell-devices/export", "fixigo_sell_device_report.xlsx", params())} />
        </div>
      </div>

      <div className="border border-border rounded-sm bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead>Booking Id</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Product Type</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Contact No</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Pincode</TableHead>
              <TableHead>City</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Condition (Customer)</TableHead>
              <TableHead>Condition (Partner)</TableHead>
              <TableHead className="text-right">Price (Customer)</TableHead>
              <TableHead className="text-right">Amount Payable</TableHead>
              <TableHead>Partner/Exec</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="sell-report-table">
            {loading && <TableRow><TableCell colSpan={15} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={15} className="text-center py-8 text-muted-foreground">No sell orders found</TableCell></TableRow>}
            {!loading && filtered.map((r, i) => (
              <TableRow key={r.booking_id + i} data-testid={`sell-report-row-${i}`}>
                <TableCell className="text-sm font-mono font-semibold">{r.booking_id || "—"}</TableCell>
                <TableCell className="text-sm">{r.category || "—"}</TableCell>
                <TableCell className="text-sm">{r.product_type || "—"}</TableCell>
                <TableCell className="text-sm font-medium">{r.customer_name || "—"}</TableCell>
                <TableCell className="text-sm">{r.contact_no || "—"}</TableCell>
                <TableCell className="text-xs max-w-[160px] truncate" title={r.address}>{r.address || "—"}</TableCell>
                <TableCell className="text-sm">{r.pincode || "—"}</TableCell>
                <TableCell className="text-sm">{r.city || "—"}</TableCell>
                <TableCell className="text-sm">{r.state || "—"}</TableCell>
                <TableCell className="text-xs max-w-[200px] truncate" title={r.condition_customer}>{r.condition_customer || "—"}</TableCell>
                <TableCell className="text-xs max-w-[200px] truncate" title={r.condition_partner}>{r.condition_partner || "—"}</TableCell>
                <TableCell className="text-right font-mono">{fmtINR(r.price_customer)}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{r.amount_payable === "" ? "—" : fmtINR(r.amount_payable)}</TableCell>
                <TableCell className="text-sm">{r.partner_name || "—"}</TableCell>
                <TableCell><StatusBadge value={r.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
